import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import SetPassword from './pages/SetPassword';
import DeclarationsSetup from './pages/DeclarationsSetup';
import DeclarationsGate from './components/DeclarationsGate';
import Dashboard from './pages/Dashboard';
import IntakeFormV2 from './pages/IntakeFormV2';
import Form80 from './pages/Form80';
import ThankYou from './pages/ThankYou';
import Documents from './pages/Documents';
import Messages from './pages/Messages';
import Matters from './pages/Matters';
import CreateMatter from './pages/CreateMatter';
import MatterDetailLayout from './pages/MatterDetail';
import MatterOverview from './pages/MatterOverview';
import { NotesTab, TasksTab, TimelineTab, LodgementTab } from './pages/MatterTabPlaceholder';
import Deadlines from './pages/Deadlines';
import ContactProfile from './pages/ContactProfile';
import AccountSettings from './pages/AccountSettings';
import PortalLayout from './components/PortalLayout';
import AuthGuard from './components/AuthGuard';
import AdminPageLoader from './components/AdminPageLoader';

// Admin-only routes — lazy-loaded so client users never download these chunks.
const SchemaPreview = lazy(() => import('./pages/SchemaPreview'));
const FormsReviewIndex = lazy(() => import('./pages/FormsReview'));
const FormsReviewDetail = lazy(() =>
  import('./pages/FormsReview').then((m) => ({ default: m.FormsReviewDetail })),
);
const GhlWebhookFailures = lazy(() => import('./pages/admin/GhlWebhookFailures'));
const FaqReview = lazy(() => import('./pages/admin/FaqReview'));
const FaqReviewDetail = lazy(() => import('./pages/admin/FaqReviewDetail'));
const ApprovedPdfs = lazy(() => import('./pages/admin/ApprovedPdfs'));
const ApprovedPdfsDetail = lazy(() => import('./pages/admin/ApprovedPdfsDetail'));
const AdminSettings = lazy(() => import('./pages/admin/Settings'));

function withAdminSuspense(node: React.ReactNode) {
  return <Suspense fallback={<AdminPageLoader />}>{node}</Suspense>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/set-password" element={<SetPassword />} />
      {/* Setup task #1: the declarations. Inside AuthGuard (so we know who they are) but OUTSIDE
          PortalLayout, so a client who has not accepted yet cannot see the portal nav and click
          into a form around the gate. */}
      <Route
        path="/welcome"
        element={
          <AuthGuard>
            <DeclarationsSetup />
          </AuthGuard>
        }
      />
      <Route
        element={
          <AuthGuard>
            <DeclarationsGate>
              <PortalLayout />
            </DeclarationsGate>
          </AuthGuard>
        }
      >
        <Route path="/matters" element={<Matters />} />
        <Route path="/matters/archived" element={<Matters mode="archived" />} />
        <Route path="/matters/new" element={<CreateMatter />} />
        <Route path="/contacts/:id" element={<ContactProfile />} />
        <Route path="/admin/forms-review" element={withAdminSuspense(<FormsReviewIndex />)} />
        <Route path="/admin/forms-review/:slug" element={withAdminSuspense(<FormsReviewDetail />)} />
        <Route path="/admin/schema-preview" element={withAdminSuspense(<SchemaPreview />)} />
        <Route path="/admin/ghl-webhook-failures" element={withAdminSuspense(<GhlWebhookFailures />)} />
        <Route path="/admin/faq-review" element={withAdminSuspense(<FaqReview />)} />
        <Route path="/admin/faq-review/:id" element={withAdminSuspense(<FaqReviewDetail />)} />
        <Route path="/admin/approved-pdfs" element={withAdminSuspense(<ApprovedPdfs />)} />
        <Route path="/admin/approved-pdfs/:id" element={withAdminSuspense(<ApprovedPdfsDetail />)} />
        <Route path="/admin/settings" element={withAdminSuspense(<AdminSettings />)} />
        <Route path="/deadlines" element={<Deadlines />} />
        {/* Matter Detail with nested tab routes. Default tab = Overview. */}
        <Route path="/matters/:id" element={<MatterDetailLayout />}>
          <Route index element={<MatterOverview />} />
          <Route path="intake" element={<IntakeFormV2 />} />
          <Route path="form-80" element={<Form80 />} />
          <Route path="documents" element={<Documents />} />
          <Route path="messages" element={<Messages />} />
          <Route path="notes" element={<NotesTab />} />
          <Route path="tasks" element={<TasksTab />} />
          <Route path="timeline" element={<TimelineTab />} />
          <Route path="lodgement" element={<LodgementTab />} />
        </Route>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/account" element={<AccountSettings />} />
        {/* Client-scoped routes (used by existing client links). Intake is the
            schema-driven form — the single source of truth for all audiences. */}
        <Route path="/intake" element={<IntakeFormV2 />} />
        <Route path="/form-80" element={<Form80 />} />
        <Route path="/documents" element={<Documents />} />
        <Route path="/messages" element={<Messages />} />
        <Route path="/thank-you" element={<ThankYou />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

function NotFound() {
  return (
    <div className="max-w-xl mx-auto py-16 text-center">
      <div className="text-5xl font-bold text-[#0B2341] mb-3">404</div>
      <p className="text-slate-600 mb-6">
        We couldn't find that page. The link may be out of date or mistyped.
      </p>
      <a
        href="/dashboard"
        className="inline-flex items-center px-4 py-2 rounded-md bg-[#2CABEA] text-white font-semibold hover:bg-[#0B2341]"
      >
        Back to dashboard
      </a>
    </div>
  );
}
